/**
 * [TD-47.2b + TD-47.3] SnakeCenterSurface - adaptive center pane for the
 * snake shell.
 *
 * v1.2-TD-47.2b states:
 *   • YOUR TURN - full cinematic: ON THE CLOCK header + giant timer +
 *     pick number/round context. [Item 2] The one-off "Autopick
 *     (double-tap)" button is gone: the queue is the autodraft surface now
 *     (toggle + per-row Draft pills live in the right-rail Queue panel).
 *   • SCOUTING MODE (NOT YOUR TURN) - manager-on-clock context + relocated
 *     OnDeckIndicator (Gap G.1) + latest Adam take when available.
 *
 * v1.2-TD-47.3 (Gap 1 verdict D - Hybrid):
 *   • YOUR TURN gains a "Top of Queue" card. Click → publishes
 *     `setSelectedPlayerId` via DraftFiltersContext; SnakePlayerPool reads
 *     it and opens the SnakeActionDrawer pre-filled. User still confirms
 *     via a single DRAFT click (§3.2 - queue does NOT auto-pick).
 *   • SCOUTING MODE gains a mini 3-row queue preview with a "Manage queue
 *     in right rail →" hint. Full management lives in the 4th right-pane
 *     tab; center pane shows ambient signal only.
 */
import React, { useEffect, useState } from "react";
import { Zap, ListChecks, ArrowRight, PauseCircle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSnakeDraft } from "../../contexts/SnakeDraftContext";
import { useDraftFilters } from "../../contexts/DraftFiltersContext";
import { useDraftQueueContext } from "../../contexts/DraftQueueContext";
import OnDeckIndicator from "../snake/OnDeckIndicator";
import ManagerName from "../ManagerName";
import PositionBadge from "../PositionBadge";
import { formatClock, isUntimed } from "../../utils/pickTimer";


/** Local countdown ticker driven by timer_remaining + timer_version
 *  (matches the OnTheClockPanel pattern; preserves [C7] reset behavior). */
function useCountdown(initial, version) {
  const [t, setT] = useState(initial || 0);
  useEffect(() => {
    setT(initial || 0);
    if (!initial || initial <= 0) return;
    const id = setInterval(() => {
      setT((x) => (x > 0 ? x - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [initial, version]);
  return t;
}

export default function SnakeCenterSurface({ draftState, leagueId, isDark }) {
  const { user } = useAuth();
  const { latestAdamMessage } = useSnakeDraft();
  const { setSelectedPlayerId } = useDraftFilters();
  const { queue, topOfQueue } = useDraftQueueContext();

  const currentPickerId = draftState?.current_picker_id;
  const picker = currentPickerId ? draftState?.managers?.[currentPickerId] : null;
  const isMe = currentPickerId === user?.id;
  // [SNAKE TIMER REV] An untimed league has no clock at all - show the state,
  // not a frozen 0.
  const untimed = isUntimed(draftState);
  const timerRemaining = useCountdown(draftState?.timer_remaining, draftState?.timer_version);
  const totalPicks = (draftState?.draft_order?.length || 0) * (draftState?.roster_size || 14);
  const overallPick = (draftState?.total_picks_made || 0) + 1;
  const timerColor = timerRemaining <= 10 ? "#FF4444" : "#FF6B00";
  const isPaused = draftState?.state === "PAUSED";


  const cardBg = isDark ? "#1C1C1C" : "#FFFFFF";
  const border = isDark ? "#252525" : "#D8D8D8";
  const muted = isDark ? "#A0A0A0" : "#404040";
  const name = isDark ? "#F5F5F5" : "#0A0A0A";

  // [TD-47.3] Top-of-Queue / mini preview click → pre-fill SnakePlayerPool
  // drawer via the lifted DraftFiltersContext.selectedPlayerId broker.
  const handleQueuedPlayerClick = (player) => {
    if (!player?.id) return;
    setSelectedPlayerId?.(player.id);
  };

  return (
    <div
      className="rounded-2xl border p-5 flex flex-col gap-4 h-full overflow-y-auto"
      style={{ background: cardBg, borderColor: border }}
      data-testid="snake-center-surface"
      data-mode={isMe ? "your-turn" : "scouting"}
    >
      {/* [P1 reconnect] A paused draft has to SAY so. A client that joins
          mid-pause otherwise sees a frozen clock and assumes it is broken -
          `state` was in every snapshot already, nothing rendered it. */}
      {isPaused && (
        <div
          className="rounded-xl border px-3 py-2 flex items-center gap-2"
          style={{ background: "rgba(255,107,0,0.10)", borderColor: "rgba(255,107,0,0.45)" }}
          data-testid="snake-center-paused-banner"
        >
          <PauseCircle size={15} className="text-[#FF6B00] shrink-0" />
          <p className="font-display font-black text-[11px] uppercase tracking-widest text-[#FF6B00]">
            Draft paused by the commissioner
          </p>
        </div>
      )}
      {/* Header - On the clock (always shown; "ON THE CLOCK" copy regardless of who's picking) */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-[#FF6B00]" />
            <span
              className="font-display font-black text-[10px] uppercase tracking-widest"
              style={{ color: muted }}
            >
              On the Clock
            </span>
          </div>
          <h1
            className="font-display font-black text-2xl sm:text-3xl uppercase leading-tight truncate"
            style={{ color: name }}
            data-testid="snake-center-current-picker-name"
          >
            {picker ? <ManagerName manager={picker} /> : "-"}
          </h1>
          <p className="font-body text-xs mt-1" style={{ color: muted }}>
            Pick {overallPick}{totalPicks ? ` of ${totalPicks}` : ""} ·
            Round {draftState?.current_round || "-"}
          </p>
        </div>
        <div
          className="text-right shrink-0"
          data-testid="snake-center-timer"
          data-untimed={untimed ? "true" : "false"}
          style={{ color: untimed ? muted : timerColor }}
        >
          {untimed ? (
            <>
              <p className="font-display font-black text-xl sm:text-2xl uppercase leading-none">
                No Time
              </p>
              <p
                className="font-display font-bold text-[9px] uppercase tracking-widest mt-1"
                style={{ color: muted }}
              >
                Limit
              </p>
            </>
          ) : (
            <>
              <p className="font-mono font-black text-4xl sm:text-5xl leading-none tabular-nums">
                {formatClock(timerRemaining)}
              </p>
              <p
                className="font-display font-bold text-[9px] uppercase tracking-widest mt-1"
                style={{ color: muted }}
              >
                remaining
              </p>
            </>
          )}
        </div>
      </div>

      {/* YOUR TURN - Top of Queue (TD-47.3 Gap 1.D) */}
      {isMe ? (
        <div className="flex flex-col gap-3" data-testid="snake-center-your-turn">
          {/* Top of Queue card (only when queue non-empty) */}
          {topOfQueue ? (
            <button
              type="button"
              onClick={() => handleQueuedPlayerClick(topOfQueue)}
              data-testid="snake-center-top-of-queue"
              className="w-full text-left rounded-xl border border-[#00C853]/40 bg-[#00C853]/10 hover:bg-[#00C853]/15 transition-colors p-3"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <ListChecks size={11} className="text-[#00C853]" />
                <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#00C853]">
                  Top of Queue · Tap to draft
                </span>
              </div>
              <div className="flex items-center gap-2">
                <PositionBadge position={topOfQueue.position} size="sm" />
                <p
                  className="font-display font-black text-lg leading-tight truncate flex-1"
                  style={{ color: name }}
                  data-testid="snake-center-top-of-queue-name"
                >
                  {topOfQueue.name}
                </p>
                <span className="font-mono font-bold text-xs shrink-0" style={{ color: muted }}>
                  {topOfQueue.nfl_team}
                </span>
              </div>
            </button>
          ) : null}

        </div>
      ) : (
        // SCOUTING MODE - Adam take + relocated OnDeckIndicator + mini queue preview
        <div className="flex flex-col gap-3" data-testid="snake-center-scouting">
          <OnDeckIndicator draftState={draftState} isDark={isDark} />
          {latestAdamMessage?.text && (
            <div
              className="rounded-xl border p-3"
              style={{ background: cardBg, borderColor: border }}
              data-testid="snake-center-adam-take"
            >
              <p
                className="font-display font-black text-[10px] uppercase tracking-widest mb-1.5"
                style={{ color: "#FF6B00" }}
              >
                Adam's Take
              </p>
              <p className="font-body text-sm" style={{ color: name }}>
                {latestAdamMessage.text}
              </p>
            </div>
          )}

          {/* TD-47.3 Gap 1.D - mini queue preview (top 3) when non-empty */}
          {queue.length > 0 ? (
            <div
              className="rounded-xl border p-3"
              style={{ background: cardBg, borderColor: border }}
              data-testid="snake-center-queue-preview"
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <ListChecks size={11} className="text-[#FF6B00]" />
                  <span
                    className="font-display font-black text-[10px] uppercase tracking-widest"
                    style={{ color: "#FF6B00" }}
                  >
                    Your Queue · Top {Math.min(queue.length, 3)}
                  </span>
                </div>
                <span
                  className="text-[10px] font-mono tabular-nums"
                  style={{ color: muted }}
                  data-testid="snake-center-queue-preview-count"
                >
                  {queue.length}
                </span>
              </div>
              <div className="space-y-1">
                {queue.slice(0, 3).map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleQueuedPlayerClick(p)}
                    data-testid={`snake-center-queue-preview-row-${p.id}`}
                    className="w-full flex items-center gap-2 text-left rounded px-1.5 py-1 hover:bg-[#FF6B00]/10 transition-colors"
                  >
                    <span
                      className="font-mono text-[9px] tabular-nums w-3 shrink-0"
                      style={{ color: muted }}
                    >
                      {idx + 1}
                    </span>
                    <PositionBadge position={p.position} size="sm" />
                    <p
                      className="font-body text-xs truncate flex-1"
                      style={{ color: name }}
                    >
                      {p.name}
                    </p>
                  </button>
                ))}
              </div>
              <p
                className="flex items-center gap-1 font-body text-[10px] mt-2"
                style={{ color: muted }}
              >
                Manage in right rail
                <ArrowRight size={9} />
              </p>
            </div>
          ) : (
            <p
              className="font-body text-[11px] text-center"
              style={{ color: muted }}
              data-testid="snake-center-scouting-hint"
            >
              Use this time to scout - pick history's in the right rail.
            </p>
          )}
        </div>
      )}

    </div>
  );
}
